import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import axiosInstance from "../../redux/network/https";
import { endPoints, queryKeys } from "../../redux/network/constant";
import supportArticles from "../../data/supportArticles.json";

const unwrap = (response) => response?.data?.data ?? response?.data;

const fetchSupportArticles = async (category) =>
  category
    ? supportArticles.filter((article) => article.category === category)
    : supportArticles;

export const useSupportArticles = (category = "", enabled = true) =>
  useQuery({
    queryKey: queryKeys.SupportArticles(category),
    queryFn: () => fetchSupportArticles(category),
    enabled,
    staleTime: Infinity,
  });

const fetchSupportSubmissions = async () => {
  const { data } = await axiosInstance.get(endPoints.SupportSubmissions);
  return unwrap(data) || { items: [], count: 0 };
};

export const useSupportSubmissions = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.SupportSubmissions(),
    queryFn: fetchSupportSubmissions,
    enabled,
    staleTime: 15 * 1000,
  });

const fetchSupportSubmission = async (publicId) => {
  const { data } = await axiosInstance.get(
    endPoints.SupportSubmissionDetail(publicId),
  );
  return unwrap(data);
};

export const useSupportSubmission = (publicId, enabled = true) =>
  useQuery({
    queryKey: queryKeys.SupportSubmission(publicId),
    queryFn: () => fetchSupportSubmission(publicId),
    enabled: Boolean(publicId) && enabled,
  });

const createSupportDraft = async (payload) => {
  const { data } = await axiosInstance.post(endPoints.SupportSubmissions, payload);
  return unwrap(data);
};

export const useCreateSupportDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSupportDraft,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.SupportSubmissions() });
    },
  });
};

const uploadSupportScreenshot = async ({ submissionId, file }) => {
  const { data: intentResponse } = await axiosInstance.post(
    endPoints.SupportScreenshotUploadIntent(submissionId),
    {
      file_name: file.name,
      content_type: file.type,
      byte_size: file.size,
    },
  );
  const intent = unwrap(intentResponse);
  if (!intent?.upload_url || !intent?.attachment_id) {
    throw new Error("Screenshot upload could not be prepared.");
  }

  const formData = new FormData();
  Object.entries(intent.fields || {}).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append("file", file);
  try {
    const uploadResponse = await fetch(intent.upload_url, {
      method: "POST",
      body: formData,
    });
    if (!uploadResponse.ok) {
      throw new Error("Screenshot upload failed. Please try again.");
    }

    const { data: completeResponse } = await axiosInstance.post(
      endPoints.SupportScreenshotComplete(submissionId, intent.attachment_id),
    );
    return unwrap(completeResponse);
  } catch (error) {
    await axiosInstance
      .delete(endPoints.SupportScreenshotDelete(submissionId, intent.attachment_id))
      .catch(() => {});
    throw error;
  }
};

export const useUploadSupportScreenshot = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: uploadSupportScreenshot,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.SupportSubmission(variables.submissionId),
      });
    },
  });
};

const deleteSupportScreenshot = async ({ submissionId, attachmentId }) => {
  const { data } = await axiosInstance.delete(
    endPoints.SupportScreenshotDelete(submissionId, attachmentId),
  );
  return unwrap(data);
};

export const useDeleteSupportScreenshot = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSupportScreenshot,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.SupportSubmission(variables.submissionId),
      });
    },
  });
};

const submitSupportSubmission = async (publicId) => {
  const { data } = await axiosInstance.post(
    endPoints.SupportSubmissionSubmit(publicId),
  );
  return unwrap(data);
};

export const useSubmitSupportSubmission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: submitSupportSubmission,
    onSuccess: (_data, publicId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.SupportSubmissions() });
      queryClient.invalidateQueries({ queryKey: queryKeys.SupportSubmission(publicId) });
    },
  });
};

const fetchSupportAdminSubmissions = async (filters) => {
  const { data } = await axiosInstance.get(endPoints.SupportAdminSubmissions, {
    params: filters,
  });
  return unwrap(data) || { items: [], count: 0 };
};

export const useSupportAdminSubmissions = (filters = {}, enabled = true) =>
  useQuery({
    queryKey: queryKeys.SupportAdminSubmissions(filters),
    queryFn: () => fetchSupportAdminSubmissions(filters),
    enabled,
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
    refetchOnWindowFocus: true,
  });

const fetchSupportAdminSubmission = async (publicId) => {
  const { data } = await axiosInstance.get(
    endPoints.SupportAdminSubmissionDetail(publicId),
  );
  return unwrap(data);
};

const isSupportAdminListQuery = (query) =>
  query.queryKey.length === 3 &&
  typeof query.queryKey[2] === "object" &&
  query.queryKey[2] !== null;

export const useSupportAdminSubmission = (publicId, enabled = true) =>
  useQuery({
    queryKey: queryKeys.SupportAdminSubmission(publicId),
    queryFn: () => fetchSupportAdminSubmission(publicId),
    enabled: Boolean(publicId) && enabled,
  });

const updateSupportAdminSubmission = async ({ publicId, payload }) => {
  const { data } = await axiosInstance.patch(
    endPoints.SupportAdminSubmissionDetail(publicId),
    payload,
  );
  return unwrap(data);
};

export const useUpdateSupportAdminSubmission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateSupportAdminSubmission,
    onMutate: async ({ publicId, payload }) => {
      const listQueryKey = queryKeys.SupportAdminSubmissionsBase();
      const detailQueryKey = queryKeys.SupportAdminSubmission(publicId);

      await queryClient.cancelQueries({ queryKey: listQueryKey });

      const previousLists = queryClient.getQueriesData({
        queryKey: listQueryKey,
        predicate: isSupportAdminListQuery,
      });
      const previousDetail = queryClient.getQueryData(detailQueryKey);

      queryClient.setQueriesData(
        { queryKey: listQueryKey, predicate: isSupportAdminListQuery },
        (data) =>
          data?.items
            ? {
                ...data,
                items: data.items.map((item) =>
                  item.public_id === publicId ? { ...item, ...payload } : item,
                ),
              }
            : data,
      );
      queryClient.setQueryData(detailQueryKey, (data) =>
        data ? { ...data, ...payload } : data,
      );

      return { detailQueryKey, previousDetail, previousLists };
    },
    onError: (_error, _variables, context) => {
      context?.previousLists?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      if (context?.detailQueryKey) {
        queryClient.setQueryData(context.detailQueryKey, context.previousDetail);
      }
    },
    onSuccess: (data, variables) => {
      if (data) {
        queryClient.setQueryData(
          queryKeys.SupportAdminSubmission(variables.publicId),
          data,
        );
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.SupportAdminSubmissionsBase(),
        predicate: isSupportAdminListQuery,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.SupportAdminSubmission(variables.publicId),
      });
    },
  });
};

const getSupportScreenshotDownloadUrl = async ({ publicId, attachmentId }) => {
  const { data } = await axiosInstance.post(
    endPoints.SupportAdminScreenshotDownload(publicId, attachmentId),
  );
  return unwrap(data);
};

export const useSupportScreenshotDownload = () =>
  useMutation({ mutationFn: getSupportScreenshotDownloadUrl });
